import React, { startTransition, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Currency } from "../../../../js/common/CurrencyFormatter";
import { ProductDetailImageInfo, ProductDetailInfo } from "../ProductDetailPageTypeConfig";
import ProductGalleryDescriptionContent from "./ProductGalleryDescriptionContent";
import ProductModalGalleryContent from "./ProductModalGalleryContent";

interface ProductGaleryModalContentProperties {
    readonly product: ProductDetailInfo;
    readonly cheapestPrice: number;
    readonly startImageIndex: number;
    readonly actionDiscountCode: string | null;
    readonly megaClearanceSaleDiscountCode: string | null;
    readonly newProductsDiscountCode: string;
    readonly currency: Currency;

    readonly onAddToBasket: (productId: number) => void;
    readonly onProductChange: (packageUrl: string) => void;
}

const imageTranslation = new ProductModalGalleryContent();

const resolveActiveImage = (product: ProductDetailInfo, imageIndex: number): ProductDetailImageInfo | undefined => (product.images.length === 0
    ? undefined
    : (imageIndex >= product.images.length ? product.images[0] : product.images[imageIndex]));

export default function ProductGaleryModalContent({
    product,
    cheapestPrice,
    startImageIndex,
    actionDiscountCode,
    megaClearanceSaleDiscountCode,
    newProductsDiscountCode,
    currency,
    onAddToBasket,
    onProductChange,
}: ProductGaleryModalContentProperties): React.JSX.Element {
    const [imageIndex, setImageIndex] = React.useState<number>(startImageIndex);

    const { t } = useTranslation("detail");
    const mainImageContainerReference = useRef<HTMLImageElement>(null);

    const setImageTranslation = (): void => {
        if (mainImageContainerReference.current === null) {
            return;
        }

        mainImageContainerReference.current.style.transform = imageTranslation.getImageTransforms();
    };

    const handleProductChange = (event: React.MouseEvent, packageUrl: string | null): void => {
        event.preventDefault();

        if (packageUrl === null) {
            return;
        }

        onProductChange(packageUrl);
    };

    const goToImage = useCallback((index: number): void => {
        setImageIndex(index);
        imageTranslation.resetAdjust();
        setImageTranslation();
    }, []);

    const thumbnailImages = product.images.map((image: ProductDetailImageInfo) => <li
        key={`th_${image.index}`} className={image.index === imageIndex ? "active" : ""}
        onClick={(): void => goToImage(image.index)}>
        <img src={image.thumbnailImageUrl} alt="" className="img-responsive" />
    </li>);

    const mobileImages = product.images
        .map((image: ProductDetailImageInfo) => <li key={`mimg_${image.index}`}><img src={image.imageUrl} alt="" className="img-responsive" /></li>);

    useEffect(() => {
        if (imageIndex > 0 && imageIndex >= product.images.length) {
            let cancelled = false;

            startTransition(() => {
                if (!cancelled) {
                    goToImage(0);
                }
            });

            return (): void => {
                cancelled = true;
            };
        }

        return (): void => { /* empty */ };
    }, [imageIndex, product.images.length, goToImage]);

    const activeImage = resolveActiveImage(product, imageIndex);

    const increaseImageIndex = useCallback((): void => {
        if (imageIndex === product.images.length - 1) {
            goToImage(0);
        } else {
            goToImage(imageIndex + 1);
        }
    }, [goToImage, imageIndex, product.images.length]);

    const decreaseImageIndex = useCallback((): void => {
        if (imageIndex === 0) {
            goToImage(product.images.length - 1);
        } else {
            goToImage(imageIndex - 1);
        }
    }, [goToImage, imageIndex, product.images.length]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent): void => {
            switch (event.key) {
                case "ArrowLeft":
                    decreaseImageIndex();
                    break;
                case "ArrowRight":
                    increaseImageIndex();
                    break;
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        return (): void => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [decreaseImageIndex, increaseImageIndex]);

    const setImageCurrentTranslation = (): void => {
        if (mainImageContainerReference.current === null) {
            return;
        }

        mainImageContainerReference.current.style.transform = imageTranslation.getImageCurrentTransforms();
    };

    useEffect(() => {
        if (mainImageContainerReference.current === null) {
            return;
        }

        const hammer = new Hammer(mainImageContainerReference.current);
        const hammerPinch = new Hammer.Pinch();
        const hammerPan = new Hammer.Pan();

        hammerPinch.recognizeWith(hammerPan);
        hammer.add([hammerPinch, hammerPan]);

        mainImageContainerReference.current.addEventListener("touchstart", (event: TouchEvent): void => {
            event.preventDefault();
        });

        hammer.get("pinch").set({ enable: true });

        hammer.on("pan pinch", (event: HammerInput): void => {
            if (mainImageContainerReference.current === null) {
                return;
            }

            imageTranslation.shiftCurrentTransforms(event.scale, event.deltaX, event.deltaY);
            setImageCurrentTranslation();
        });

        hammer.on("pan pinchend", (): void => {
            if (mainImageContainerReference.current === null) {
                return;
            }

            imageTranslation.moveCurrentTransformsToAdjust(mainImageContainerReference.current.clientWidth, mainImageContainerReference.current.clientHeight);
            setImageTranslation();
        });

        imageTranslation.resetAdjust();
        setImageCurrentTranslation();

        return (): void => {
            hammer.destroy();
        };
    }, []);

    return (
        <div>
            <div className="gallery-modal-left">
                {product.previousPackageUrl !== null
                  && <div className="blue-text">
                      <a
                          href={product.previousPackageUrl}
                          onClick={(event): void => handleProductChange(event, product.previousPackageUrl)}>&#x1F448;<br />
                          <span>{t("showPreviousProduct")}</span>
                      </a>
                  </div>}

                <p className="fakeh3">{product.title}</p>
                <div className="gray">{t("productVariant")} {product.productId}</div>

                <ul className="list-unstyled gallery-control">
                    {thumbnailImages}
                </ul>
            </div>
            <div className="image">
                <div className="mobile-next-prev hidden">
                    {product.previousPackageUrl !== null && <div className="blue-text">
                        <a
                            href={product.previousPackageUrl}
                            onClick={(event): void => handleProductChange(event, product.previousPackageUrl)}>
                            &#x1F448;<br />
                            <span>{t("previousProduct")}<br />{t("fromTheSameCategory")}</span>
                        </a>
                    </div>}

                    {product.nextPackageUrl !== null
                      && <div className="blue-text">
                          <a
                              href={product.nextPackageUrl}
                              onClick={(event): void => handleProductChange(event, product.nextPackageUrl)}>
                              &#x1F449;<br />
                              <span>{t("nextProduct")}<br />{t("fromTheSameCategory")}</span>
                          </a>
                      </div>}
                </div>

                <div className="image-container">
                    <div className="image-caption image-caption-change gallery-image" ref={mainImageContainerReference}>
                        {activeImage !== undefined && <>
                            <img src={activeImage.imageUrl} alt="" />
                            <div className="loader">
                                <div className="loader-inner"></div>
                                {t("loadingImage")}
                            </div>
                        </>}

                        {product.hasRealPhoto
                            ? <span>{t("realPhoto")}</span>
                            : <span>{t("illustrativePhoto")}</span>}
                    </div>
                </div>

                <div className="gallery-note hidden">{t("zoomPhoto")}</div>

                <button type="button" className="btn gallery-control left" onClick={(): void => decreaseImageIndex()}>
                    <i className="fa-solid fa-caret-left"></i>
                </button>

                <button type="button" className="btn gallery-control right" onClick={(): void => increaseImageIndex()}>
                    <i className="fa-solid fa-caret-right"></i>
                </button>

                <div className="gallery-mobile-control hidden">
                    <ul>
                        {mobileImages}
                    </ul>
                </div>
            </div>

            <div className="gallery-modal-right">

                {product.nextPackageUrl !== null
                  && <div className="blue-text">
                      <a
                          href={product.nextPackageUrl}
                          onClick={(event): void => handleProductChange(event, product.nextPackageUrl)}>
                          &#x1F449;<br />
                          <span>{t("showNextProduct")}<br />
                              {t("fromTheSameCategory")}
                          </span>
                      </a>
                  </div>}

                <ProductGalleryDescriptionContent
                    product={product}
                    cheapestPrice={cheapestPrice}
                    newProductsDiscountCode={newProductsDiscountCode}
                    actionDiscountCode={actionDiscountCode}
                    megaClearanceSaleDiscountCode={megaClearanceSaleDiscountCode}
                    currency={currency}
                    onAddToBasket={onAddToBasket}
                />
            </div>
        </div>
    );
}
